// src/components/collections/CollectionRevealCard.tsx
//
// The inline chat card that surfaces a collection find. Renders
// directly inside the message thread (MessageBubble dispatches on
// the `collection_reveal` part type). Visual goals:
//
//   - Distinct from a normal AI bubble — it's a moment, not chatter
//   - Lead with the IMAGE (user's photo OR Wikipedia fallback)
//   - The AI prose IS the headline; progress is a subtle counter
//   - Tappable follow-up CTA at the bottom — either sends the
//     suggested follow-up back into chat via onFollowUpClick OR,
//     when the server pre-armed a NAVIGATE_TO action on the
//     reveal, renders an inline NavigateToCard (pulldown map +
//     walking route + "Open in Google Maps"). The pre-armed
//     path closes the loop that broke in the post-engagement
//     Rome conversation: user tapped "Head up to the Palatine?"
//     and the chat AI didn't emit a NAVIGATE_TO block, leaving
//     the user without a map.
//
// We deliberately don't render hidden-total progress as "X / ?" —
// the prose already says "more out there"; the counter becomes
// just "found X" instead of "X of Y". This matches the user's
// brainstorm intent: hidden totals subvert completionism.

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  useTheme,
} from '@mui/material';
import {
  AutoAwesome,
  CheckCircle,
  ArrowForward,
  Directions,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useResolvedImageUrl } from '../../feed/useResolvedImageUrl';
import { useT } from '../../i18n/useT';
import { CollectionRevealMessagePart } from '../../types/collections';
import { NavigateToCard } from '../navigate/NavigateToCard';
import { getFunctions, httpsCallable } from 'firebase/functions';

interface Props {
  data: CollectionRevealMessagePart['data'];
  onFollowUpClick?: (prompt: string) => void;
}

export const CollectionRevealCard: React.FC<Props> = ({
  data,
  onFollowUpClick,
}) => {
  const t = useT();
  const theme = useTheme();
  const navigate = useNavigate();

  // Image priority: server-stamped image_url (already resolved) →
  // photo_reference (storage path, needs resolution) → collection
  // cover. The resolver hook accepts both URLs and storage paths
  // and short-circuits on URLs.
  const rawImage =
    data.image_url ||
    data.photo_reference ||
    data.collection_cover_image_url ||
    null;
  const resolvedImage = useResolvedImageUrl(rawImage || undefined);

  const isHidden = data.collection_hidden_total;
  const isCompleted = !!data.completed;

  // Controlled state for the NavigateTo bottom sheet. The chip
  // below opens it when tapped; NavigateToCard runs in
  // controlled+hideTeaser mode (no inline teaser, drawer
  // controlled by this parent).
  const [navigateOpen, setNavigateOpen] = React.useState(false);

  // "X of Y" or "X found" depending on hidden_total.
  const progressLine = isHidden
    ? t('{count} found', { count: data.found_count_at_find })
    : t('{found} of {total}', {
        found: data.found_count_at_find,
        total: data.collection_total_items,
      });

  const handleOpen = () => {
    navigate('/collections');
  };

  // Pre-armed action: server resolved the reveal's follow_up to
  // a concrete walkable target with coordinates. When present we
  // render NavigateToCard directly so tapping opens the map
  // without the chat AI having to re-derive the routing.
  const followUpAction = (data as any).reveal_follow_up_action as
    | {
        type: 'navigate_to';
        name: string;
        lat: number;
        lng: number;
        item_id: string;
        reasoning: string;
      }
    | null
    | undefined;
  const hasNavigateAction = !!followUpAction && followUpAction.type === 'navigate_to';

  const handleFollowUp = () => {
    if (data.reveal_follow_up && onFollowUpClick) {
      onFollowUpClick(data.reveal_follow_up);
    }
  };

  // When the reveal carries a pre-armed NAVIGATE_TO, fire a
  // server-side breadcrumb on first render so the routing-
  // acceptance detector knows the user saw a tappable target.
  // The actual tap → completion correlation happens server-side
  // when (and if) a visit later lands at the target. We do this
  // in an effect rather than at tap-time so an intent is
  // recorded even if the user tapped "Open in Google Maps" and
  // we never see the tap event.
  React.useEffect(() => {
    if (!hasNavigateAction || !followUpAction) return;
    let cancelled = false;
    (async () => {
      try {
        const fn = httpsCallable(getFunctions(), 'recordRoutingIntent');
        if (cancelled) return;
        await fn({
          target_name: followUpAction.name,
          target_lat: followUpAction.lat,
          target_lng: followUpAction.lng,
          source: 'reveal_follow_up_tap',
          find_id: data.find_id,
          item_id: followUpAction.item_id,
        });
      } catch (err) {
        // Non-fatal — the message-scan acceptance path still
        // works without this breadcrumb.
        console.warn('recordRoutingIntent failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNavigateAction]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', my: 1.5 }}>
      <Paper
        elevation={0}
        sx={{
          maxWidth: 480,
          width: '100%',
          overflow: 'hidden',
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          // Subtle gradient backdrop hints "this is a special
          // moment", without screaming. Tier shifts on completion.
          background: isCompleted
            ? `linear-gradient(135deg, ${theme.palette.success.light}22, ${theme.palette.background.paper})`
            : `linear-gradient(135deg, ${theme.palette.primary.light}18, ${theme.palette.background.paper})`,
        }}
      >
        {/* Cover image — full width, ~16:9-ish */}
        {resolvedImage && (
          <Box
            sx={{
              width: '100%',
              aspectRatio: '16 / 9',
              backgroundImage: `url(${resolvedImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              position: 'relative',
            }}
          >
            {/* Pill in the top-left identifying the collection */}
            <Chip
              size="small"
              icon={<AutoAwesome sx={{ fontSize: 14 }} />}
              label={data.collection_title}
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                fontWeight: 500,
                bgcolor: 'rgba(0,0,0,0.55)',
                color: 'white',
                backdropFilter: 'blur(8px)',
                '& .MuiChip-icon': { color: 'white' },
              }}
            />

            {/* Progress badge in the top-right */}
            <Chip
              size="small"
              label={progressLine}
              icon={
                isCompleted ? (
                  <CheckCircle sx={{ fontSize: 14 }} />
                ) : undefined
              }
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                fontWeight: 600,
                bgcolor: isCompleted
                  ? theme.palette.success.main
                  : 'rgba(0,0,0,0.55)',
                color: 'white',
                backdropFilter: 'blur(8px)',
                '& .MuiChip-icon': { color: 'white' },
              }}
            />
          </Box>
        )}

        {/* Body */}
        <Box sx={{ p: 2.5 }}>
          {/* When there's NO cover image, fall back to a chip row
              above the prose so the user can still see the
              collection name and progress at a glance. */}
          {!resolvedImage && (
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                icon={<AutoAwesome sx={{ fontSize: 14 }} />}
                label={data.collection_title}
                color="primary"
                variant="outlined"
              />
              <Chip
                size="small"
                label={progressLine}
                color={isCompleted ? 'success' : 'default'}
                icon={isCompleted ? <CheckCircle sx={{ fontSize: 14 }} /> : undefined}
              />
            </Box>
          )}

          {/* The prose IS the headline. Slightly larger than body
              text so it carries weight without being a heading. */}
          <Typography
            sx={{
              fontSize: '1rem',
              lineHeight: 1.55,
              color: 'text.primary',
              mb: data.reveal_follow_up ? 2 : 0,
            }}
          >
            {data.reveal_prose}
          </Typography>

          {/* Follow-up CTA — either pre-armed map card or chat
              prompt button. The pre-armed path renders the full
              NavigateToCard (teaser + drawer + walking route)
              inline beneath the prose; the chat-prompt path is
              the original Button that just pushes the text back
              into the chat composer. */}
          {/* Follow-up CTA — either a navigate-armed chip (opens
              bottom sheet with map) or a plain chat-prompt chip.
              The two render the SAME button shape so the user
              gets a consistent reveal experience; the only visual
              difference is the leading icon on the navigate-armed
              variant (Directions icon vs ArrowForward). Tapping
              the navigate variant opens the controlled drawer
              with the live walking route. */}
          {data.reveal_follow_up && hasNavigateAction && followUpAction && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Directions sx={{ fontSize: 18 }} />}
                  onClick={() => setNavigateOpen(true)}
                  disableElevation
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    borderRadius: 2,
                  }}
                >
                  {data.reveal_follow_up}
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleOpen}
                  sx={{ textTransform: 'none', color: 'text.secondary' }}
                >
                  {t('See all')}
                </Button>
              </Box>
              {/* Controlled bottom-sheet — only renders the drawer
                  portion of NavigateToCard (hideTeaser=true). The
                  chip above is the trigger; this controls the
                  sheet's open state. */}
              <NavigateToCard
                target={{
                  name: followUpAction.name,
                  lat: followUpAction.lat,
                  lng: followUpAction.lng,
                  reasoning:
                    followUpAction.reasoning ||
                    data.reveal_follow_up ||
                    '',
                }}
                open={navigateOpen}
                onClose={() => setNavigateOpen(false)}
                hideTeaser
              />
            </>
          )}
          {data.reveal_follow_up && !hasNavigateAction && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <Button
                variant="contained"
                size="small"
                endIcon={<ArrowForward />}
                onClick={handleFollowUp}
                disableElevation
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 2,
                }}
              >
                {data.reveal_follow_up}
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={handleOpen}
                sx={{ textTransform: 'none', color: 'text.secondary' }}
              >
                {t('See all')}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

/** Companion card for the rarer "you just completed a whole
 *  collection" system message. Same shape, different tone. */
export const CollectionCompletionCard: React.FC<{
  data: {
    collection_id: string;
    collection_title: string;
    total_items: number;
    completion_postcard_id?: string;
  };
}> = ({ data }) => {
  const t = useT();
  const theme = useTheme();
  const navigate = useNavigate();
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', my: 1.5 }}>
      <Paper
        elevation={0}
        sx={{
          maxWidth: 480,
          width: '100%',
          p: 2.5,
          borderRadius: 3,
          border: `1px solid ${theme.palette.success.main}`,
          background: `linear-gradient(135deg, ${theme.palette.success.light}33, ${theme.palette.background.paper})`,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center' }}>
          <CheckCircle color="success" />
          <Typography variant="overline" sx={{ letterSpacing: 1.2 }}>
            {t('Collection complete')}
          </Typography>
        </Box>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {data.collection_title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("All {total} found. A postcard's on the way.", {
            total: data.total_items,
          })}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          color="success"
          onClick={() => navigate('/collections')}
          sx={{ textTransform: 'none' }}
        >
          {t('Open collection')}
        </Button>
      </Paper>
    </Box>
  );
};
